"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useClientLocale } from "@/lib/i18n/use-client-locale";
import confetti from "canvas-confetti";

// Step 7 commit 17 / AC239-AC242 (revised DIRECTIVE #010, 2026-05-25):
// confetti-only celebration. The full variant fires the confetti burst
// + returns null - the confirmation page itself owns the success
// indicator and action buttons now (the modal duplicated everything
// underneath it). The compact variant still toasts from the Power
// Buyer slide-to-order path; that lives on a non-confirmation route
// so the toast is the only celebration surface.

const BRAND_COLORS = ["#5BC5BA", "#ea4b78", "#E8A54A", "#3FA796"];

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export type FullCelebrationProps = {
  variant: "full";
  orderId: string;
  email: string;
  deliveryLabel: string;
  status:
    | "paid"
    | "pending_invoice"
    | "shipping_blocked"
    | "pending_payment"
    | "payment_required"
    | "blocked_payment_failed"
    // Step 8c.1a: webhook-driven post-paid transitions still render
    // the confirmation page; UI treats them as informational only.
    | "voided"
    | "deleted_in_qb"
    | "refunded"
    | "payment_voided"
    | "cancelled_by_admin"
    | "fulfilled";
};

export type CompactCelebrationProps = {
  variant: "compact";
  orderId: string;
  deliveryLabel: string;
  onDismiss?: () => void;
};

export function CelebrationOverlay(
  props: FullCelebrationProps | CompactCelebrationProps,
) {
  const [reduced, setReduced] = useState(false);
  const [mounted, setMounted] = useState(false);

  /* eslint-disable react-hooks/set-state-in-effect -- one-shot mount + media-query read */
  useEffect(() => {
    setMounted(true);
    setReduced(prefersReducedMotion());
  }, []);
  /* eslint-enable react-hooks/set-state-in-effect */

  useEffect(() => {
    if (!mounted) return;
    if (reduced) return;
    const particleCount = props.variant === "full" ? 120 : 40;
    const duration = props.variant === "full" ? 3000 : 1000;
    confetti({
      particleCount,
      spread: 70,
      origin: props.variant === "full" ? { x: 0.5, y: 0.2 } : { x: 0.92, y: 0.08 },
      colors: BRAND_COLORS,
      ticks: duration / 16,
      disableForReducedMotion: true,
    });
  }, [mounted, reduced, props.variant]);

  if (!mounted) return null;

  if (props.variant === "compact") {
    return <CompactToast {...props} reduced={reduced} />;
  }
  // Full variant is confetti-only after DIRECTIVE #010. The
  // confirmation page owns the success indicator + actions.
  return null;
}

// #81 i18n (Jul 14): the post-order toast was the last EN-only string on the
// power-buyer path - an ES client placing an order still read "Order placed!".
const TOAST = {
  en: { placed: "Order placed!", delivery: "Delivery", dismiss: "Dismiss" },
  es: { placed: "¡Pedido realizado!", delivery: "Entrega", dismiss: "Cerrar" },
} as const;

function CompactToast({
  orderId,
  deliveryLabel,
  onDismiss,
  reduced,
}: Omit<CompactCelebrationProps, "variant"> & { reduced: boolean }) {
  const [open, setOpen] = useState(true);
  const t = TOAST[useClientLocale()];
  useEffect(() => {
    const t = setTimeout(() => {
      setOpen(false);
      onDismiss?.();
    }, 5000);
    return () => clearTimeout(t);
  }, [onDismiss]);
  if (!open) return null;
  return createPortal(
    <div
      role="status"
      data-testid="celebration-overlay"
      data-variant="compact"
      data-reduced-motion={reduced ? "true" : "false"}
      className="fixed bottom-20 right-4 z-[80] flex max-w-sm items-center gap-3 rounded-xl bg-brand-success px-4 py-3 text-sm text-white shadow-2xl"
    >
      <span aria-hidden="true">🌹</span>
      <span>
        {t.placed}{" "}
        <span
          data-testid="celebration-order-id"
          className="font-semibold"
        >
          #{orderId}
        </span>{" "}
        · {t.delivery} {deliveryLabel}
      </span>
      <button
        type="button"
        aria-label={t.dismiss}
        onClick={() => {
          setOpen(false);
          onDismiss?.();
        }}
        className="text-white/70 hover:text-white"
      >
        ×
      </button>
    </div>,
    document.body,
  );
}
